import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toEmbeddableMediaUrl } from '../../utils/mediaEmbeds';

const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY || '';
const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER || 'mt1';
const PUSHER_HOST = process.env.REACT_APP_PUSHER_HOST;
const PUSHER_PORT = process.env.REACT_APP_PUSHER_PORT;
const PUSHER_SCHEME = process.env.REACT_APP_PUSHER_SCHEME || 'https';
const PLAYBACK_CHANNEL = process.env.REACT_APP_PUSHER_PLAYBACK_CHANNEL || 'tv.playback';
const resolveDefaultHomeUrl = () => {
  if (typeof window === 'undefined') return '/';
  const explicitBase = process.env.PUBLIC_URL || '';
  let appBase = explicitBase;
  if (!appBase || appBase === '/') {
    const path = window.location.pathname || '/';
    appBase = path === '/app' || path.startsWith('/app/') ? '/app' : '/';
  }
  const normalizedBase = appBase.endsWith('/') ? appBase : `${appBase}/`;
  return new URL(normalizedBase, window.location.origin).toString();
};
const PUBLIC_HOMEPAGE_URL = resolveDefaultHomeUrl();

const formatTime = (value) =>
  value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

const secondsSince = (startsAt, serverTime, receivedAt) => {
  const startsAtMs = new Date(startsAt ?? '').getTime();
  const serverTimeMs = new Date(serverTime ?? '').getTime();
  if (!Number.isFinite(startsAtMs) || !Number.isFinite(serverTimeMs)) return 0;

  const clientDriftMs = receivedAt ? Math.max(0, Date.now() - receivedAt) : 0;
  return Math.max(0, (serverTimeMs + clientDriftMs - startsAtMs) / 1000);
};

const NowPlaying = () => {
  const [state, setState] = useState({
    current: null,
    upcoming: [],
    serverTime: null,
    receivedAt: null,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const pollRef = useRef(null);
  const displayRef = useRef(null);
  const videoRef = useRef(null);
  const syncedPlaybackRef = useRef('');
  const isTvDisplay =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('display') === 'fullscreen';

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/tv/playback-state');
      setState({
        current: data.current ?? null,
        upcoming: data.upcoming ?? [],
        serverTime: data.server_time ?? data.generated_at ?? null,
        receivedAt: Date.now(),
      });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message ?? 'Unable to load playback state.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 30_000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    if (!PUSHER_KEY || typeof window === 'undefined' || !window.Pusher) {
      return undefined;
    }

    const options = {
      cluster: PUSHER_CLUSTER,
      forceTLS: PUSHER_SCHEME !== 'http',
    };

    if (PUSHER_HOST) {
      options.wsHost = PUSHER_HOST;
    }
    if (PUSHER_PORT) {
      options.wsPort = Number(PUSHER_PORT);
    }
    if (PUSHER_SCHEME) {
      options.wsScheme = PUSHER_SCHEME;
    }

    const pusher = new window.Pusher(PUSHER_KEY, options);
    const channel = pusher.subscribe(PLAYBACK_CHANNEL);
    channel.bind('ProgramPlaybackUpdated', load);

    return () => {
      channel.unbind('ProgramPlaybackUpdated', load);
      pusher.unsubscribe(PLAYBACK_CHANNEL);
      pusher.disconnect();
    };
  }, []);

  const current = state.current;
  const playbackProgram = current?.program ?? null;
  const playbackMedia = current?.media ?? playbackProgram ?? {};
  const mediaUrl = playbackMedia?.source_url ?? playbackProgram?.source_url ?? '';
  const mediaType =
    playbackProgram?.media_type ??
    (current?.source === 'archive' ? 'video' : null);
  const thumbnailUrl = playbackMedia?.thumbnail_url ?? playbackProgram?.thumbnail_url;
  const isLikelyVideoUrl = Boolean(mediaUrl) && /\.(mp4|m4v|webm|mov)(\?|#|$)/i.test(mediaUrl);
  const embedUrl = toEmbeddableMediaUrl(mediaUrl);
  const isVideo =
    Boolean(mediaUrl) &&
    !embedUrl &&
    (mediaType === 'video' || current?.source === 'archive' || isLikelyVideoUrl);
  const isEmbeddable =
    Boolean(embedUrl) ||
    (Boolean(mediaUrl) && ['web', 'playlist'].includes(mediaType ?? ''));

  const status = useMemo(() => {
    if (loading) return 'Checking…';
    if (!current) return 'Off-air';
    return 'Live';
  }, [current, loading]);

  const advanceToDueProgram = useCallback(async () => {
    const nowMs = Date.now();
    const nextSlot = state.upcoming?.find((slot) => {
      const startsAt = new Date(slot?.starts_at ?? '').getTime();
      return Number.isFinite(startsAt) && startsAt <= nowMs + 1_000;
    });

    if (nextSlot) {
      setState((previous) => ({
        current: nextSlot,
        upcoming: previous.upcoming?.filter((slot) => slot?.id !== nextSlot.id) ?? [],
        serverTime: new Date().toISOString(),
        receivedAt: Date.now(),
      }));
      setError('');
      return;
    }

    await load();
  }, [state.upcoming]);

  useEffect(() => {
    const boundary = new Date(current?.ends_at ?? '').getTime();
    if (!Number.isFinite(boundary)) return undefined;

    const timeout = setTimeout(() => {
      advanceToDueProgram().catch((err) => {
        setError(err.response?.data?.message ?? 'Unable to load the next program.');
      });
    }, Math.max(0, boundary - Date.now()) + 250);

    return () => clearTimeout(timeout);
  }, [current?.id, current?.ends_at, advanceToDueProgram]);

  useEffect(() => {
    if (!isVideo || !mediaUrl) return;
    const video = videoRef.current;
    if (!video) return;
    const playbackKey = [
      current?.source,
      current?.id ?? current?.archive?.identifier ?? 'fallback',
      current?.starts_at,
      mediaUrl,
    ].join(':');

    const syncToBroadcastTime = () => {
      if (syncedPlaybackRef.current === playbackKey) return;
      if (!current?.starts_at) return;

      const slotOffset = secondsSince(current.starts_at, state.serverTime, state.receivedAt);
      const duration = Number(video.duration || playbackMedia?.duration_seconds);
      const maxSeek = Number.isFinite(duration) && duration > 1 ? duration - 1 : slotOffset;
      const targetTime = Math.min(slotOffset, maxSeek);

      if (Number.isFinite(targetTime) && targetTime > 1) {
        video.currentTime = targetTime;
      }

      syncedPlaybackRef.current = playbackKey;
    };

    const attemptPlay = async () => {
      try {
        syncToBroadcastTime();
        await video.play();
        setAutoplayBlocked(false);
      } catch (_) {
        // Autoplay can be blocked; user gesture will be required.
        setAutoplayBlocked(true);
      }
    };

    const handleReady = () => {
      syncToBroadcastTime();
      attemptPlay();
    };

    video.addEventListener('loadedmetadata', handleReady);
    video.addEventListener('canplay', handleReady);
    if (video.readyState >= 1) {
      syncToBroadcastTime();
    }
    attemptPlay();

    return () => {
      video.removeEventListener('loadedmetadata', handleReady);
      video.removeEventListener('canplay', handleReady);
    };
  }, [
    current?.archive?.identifier,
    current?.id,
    current?.source,
    current?.starts_at,
    isVideo,
    mediaUrl,
    playbackMedia?.duration_seconds,
    state.receivedAt,
    state.serverTime,
  ]);

  const requestDisplayFullscreen = async () => {
    const target = displayRef.current;
    if (!target) return;

    if (isVideo) {
      await videoRef.current?.play().catch(() => undefined);
    }

    await target.requestFullscreen?.().catch(() => undefined);
  };

  return (
    <div
      className={`public-shell tv-display-shell${isTvDisplay ? ' tv-display-shell--fullscreen' : ''}`}
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at 20% 20%, #0d1b2a, #0a0f1a 60%)',
        color: '#f6f8fb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div className="tv-display">
        {!isTvDisplay && (
        <header className="tv-display__header">
          <div>
            <p style={{ margin: 0, opacity: 0.8 }}>Live channel · viewer display</p>
            <h1 style={{ margin: '0.2rem 0 0', fontSize: '2rem' }}>Now Playing</h1>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <a className="btn btn-muted" href={PUBLIC_HOMEPAGE_URL}>
              Home
            </a>
            <button type="button" className="btn btn-primary" onClick={load} disabled={loading}>
              {loading ? 'Updating…' : 'Refresh now'}
            </button>
            <button type="button" className="btn btn-primary" onClick={requestDisplayFullscreen}>
              Fullscreen TV
            </button>
            <Link className="btn btn-muted" to="/channel">
              Channel
            </Link>
          </div>
        </header>
        )}

        {error && <div className="error-banner">{error}</div>}

        <div
          ref={displayRef}
          className={`card tv-display__card${isTvDisplay ? ' tv-display__card--fullscreen' : ''}`}
          style={{
            background: 'linear-gradient(120deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 15px 60px rgba(0,0,0,0.35)',
          }}
        >
          <div className="tv-display__grid">
            <div className="tv-display__stage">
              {isVideo ? (
                <>
                  <video
                    ref={videoRef}
                    src={mediaUrl}
                    poster={thumbnailUrl}
                    className="tv-display__media"
                    controls
                    autoPlay
                    playsInline
                    onEnded={() => {
                      advanceToDueProgram().catch((err) => {
                        setError(err.response?.data?.message ?? 'Unable to load the next program.');
                      });
                    }}
                  />
                  {autoplayBlocked && (
                    <button
                      type="button"
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'grid',
                        placeItems: 'center',
                        background: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        fontSize: '1.05rem',
                        fontWeight: 600,
                        border: 'none',
                        cursor: 'pointer',
                      }}
                      className="broadcast-autoplay"
                      onClick={async () => {
                        const video = videoRef.current;
                        if (!video) return;
                        try {
                          await video.play();
                          setAutoplayBlocked(false);
                        } catch (_) {
                          setAutoplayBlocked(true);
                        }
                      }}
                    >
                      Tap to play
                    </button>
                  )}
                </>
              ) : isEmbeddable ? (
                <iframe
                  key={embedUrl ?? mediaUrl}
                  src={embedUrl ?? mediaUrl}
                  title={current?.title ?? playbackProgram?.title ?? 'Embedded program'}
                  className="tv-display__media"
                  frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div
                  style={{
                    height: '100%',
                    minHeight: 280,
                    display: 'grid',
                    placeItems: 'center',
                    background: 'rgba(0,0,0,0.4)',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ margin: 0, opacity: 0.8 }}>No video preview</p>
                    <p style={{ margin: 0, fontSize: '0.95rem' }}>
                      {mediaUrl ? 'Source is not embeddable in preview' : 'Awaiting next program'}
                    </p>
                  </div>
                </div>
              )}
              <div className="tv-display__status">
                {status}
              </div>
            </div>

            {!isTvDisplay && (
            <div className="tv-display__meta">
              <div>
                <p className="text-muted" style={{ marginBottom: '0.2rem' }}>
                  {current?.type === 'live' ? 'Live window' : 'Program'}
                </p>
                <h2 style={{ margin: 0, fontSize: '1.35rem' }}>
                  {current?.title ?? playbackProgram?.title ?? 'Off-air'}
                </h2>
                {(playbackProgram?.description || current?.description) && (
                  <p style={{ marginTop: '0.35rem', color: 'rgba(255,255,255,0.75)' }}>
                    {playbackProgram?.description ?? current?.description}
                  </p>
                )}
              </div>

              <div className="pill-row">
                <button type="button" className="btn btn-primary" onClick={requestDisplayFullscreen}>
                  Start fullscreen
                </button>
                <span className="pill">Starts {formatTime(current?.starts_at)}</span>
                <span className="pill">Ends {formatTime(current?.ends_at)}</span>
                <span className="pill">{mediaType ?? current?.type ?? '—'}</span>
              </div>

              {state.upcoming?.length > 0 && (
                <div>
                  <p className="text-muted" style={{ marginBottom: '0.35rem' }}>
                    Up next
                  </p>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.4rem' }}>
                    {state.upcoming.map((item) => (
                      <li
                        key={item.id}
                        style={{
                          padding: '0.55rem 0.65rem',
                          borderRadius: '10px',
                          background: 'rgba(255,255,255,0.05)',
                        }}
                      >
                        <strong>{item.program?.title ?? item.title ?? 'Program'}</strong>
                        <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)' }}>
                          {formatTime(item.starts_at)} · {item.type === 'live' ? 'Live' : 'Program'}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NowPlaying;
